import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { Tabs } from '@client/components/_core/navigation/Tabs';
import { TextAreaWithAiAssistant } from '@client/components/_shared/ai/TextAreaWithAiAssistant';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { modal } from '@client/components/_ui/feedback/modal';
import { FinancialDetailsHeader } from '@client/components/claims/financials/FinancialDetailsHeader';
import {
    EXCESS_CONTRIBUTION_METHODS_LABELS,
    EXCESS_STATUSES_LABELS,
} from '@client/components/claims/financials/translations';
import { ICONS } from '@client/constants/icons';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { trpc } from '@client/trpc/client';
import { EXCESS_STATUSES, type ExcessStatus } from '@shared/constants/business/excess';
import { createFileRoute } from '@tanstack/react-router';
import { Form, Skeleton, Tag, Tooltip, Typography } from 'antd';
import { CreditCard, DollarSign, FileText } from 'lucide-react';
import type { ComponentProps } from 'react';
import { match } from 'ts-pattern';
import { excessFormStore } from './store';

export const Route = createFileRoute('/console/$orgId/claims/details/$id/financials/excesses/details/$excessId')({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Excess details',
        },
    },
    params: {
        parse({ excessId }) {
            return {
                excessId: BigInt(excessId),
            };
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ExcessDetails />
        </PageController>
    );
}

const colors: Record<ExcessStatus, ComponentProps<typeof Tag>['color']> = {
    FULLY_SETTLED: 'success',
    PENDING: 'blue',
    UNRESOLVED: 'default',
    PARTIALLY_SETTLED: 'warning',
    WAIVED: 'volcano',
    CANCELLED: 'error',
};

type DismissForm = {
    statusReason: string;
};

function ExcessDetails() {
    const orgId = useOrgId();
    const { id: claimId, excessId } = Route.useParams();
    const { data, isPending, isRefetching, refetch } = trpc.excess.getExcess.useQuery(
        { id: excessId },
        {
            throwOnError(error) {
                match(error.data?.errorId)
                    .with('recordNotFound', () => {
                        void errorMessage
                            .showAsync(
                                "The excess you are trying to access either doesn't exist or you don't have permissions to access it.",
                            )
                            .then(() =>
                                navigate({
                                    to: '/console/$orgId/claims/details/$id/financials',
                                    params: {
                                        orgId,
                                        id: claimId,
                                    },
                                }),
                            );
                    })
                    .otherwise(() => errorMessage.show(error));

                return false;
            },
        },
    );
    const { trpcUtils } = useTrpc();
    const { mutate, mutateAsync } = trpc.excess.updateExcess.useMutation();
    const navigate = useNavigate();

    const [dismissForm] = Form.useForm<DismissForm>();

    const { formData: excess } = excessFormStore.useInit({
        entity: 'excess',
        data,
        isLoading: isPending || isRefetching,

        async onReload() {
            await refetch();
        },

        async onSave(currentFormData, newValue) {
            try {
                await mutateAsync({ id: currentFormData.id, set: newValue });
                await trpcUtils.excess.invalidate();
                void trpcUtils.claim.getFinancialSummary.invalidate();

                return { success: true };
            } catch (e) {
                return { success: false, error: e };
            }
        },

        async onError(error) {
            errorMessage.show(error);
        },
    });

    return (
        <OverlayPage
            title={
                <div className="flex items-center gap-2">
                    <ICONS.excess className="card-title-icon size-5" />
                    <span>Excess details</span>
                </div>
            }
            size="xLarge"
            afterClose={() => navigate({ moveInMatches: -1 })}
            footer={({ onClose }) => <Button onClick={onClose}>Cancel</Button>}
        >
            {excessId !== excess?.id ? (
                <Skeleton active />
            ) : (
                <>
                    <FinancialDetailsHeader
                        data={{
                            costCode: excess?.costCodeFullName,
                            createdAt: excess?.createdAt,
                            CreatedBy: {
                                userId: excess?.CreatedBy?.userId,
                                photoUrl: excess?.CreatedBy?.photoUrl,
                                fullName: excess?.CreatedBy?.fullName,
                            },
                        }}
                        rightAddon={[EXCESS_STATUSES.CANCELLED, EXCESS_STATUSES.WAIVED].map((item) => (
                            <Tooltip
                                key={item}
                                title={
                                    excess?.isReadOnly &&
                                    `This excess cannot be ${item === 'CANCELLED' ? 'cancelled' : 'waived'} since it has already been cancelled, waived, or has existing contributions.`
                                }
                            >
                                <Button
                                    type="tertiary"
                                    disabled={excess?.isReadOnly}
                                    onClick={() =>
                                        modal.confirm({
                                            title: `${item === 'CANCELLED' ? 'Cancel' : 'Waive'} excess`,
                                            width: 500,
                                            content: (
                                                <>
                                                    <Typography.Paragraph type="warning">
                                                        Are you sure you would like to{' '}
                                                        {item === 'CANCELLED' ? 'cancel' : 'waive'} this excess?
                                                    </Typography.Paragraph>
                                                    <Form preserve={false} layout="vertical" form={dismissForm}>
                                                        <Form.Item<DismissForm>
                                                            label="Please provide a reason"
                                                            name="statusReason"
                                                            rules={[
                                                                {
                                                                    required: true,
                                                                    message: 'Field is required',
                                                                },
                                                            ]}
                                                            className="mb-0"
                                                        >
                                                            <TextAreaWithAiAssistant
                                                                rows={4}
                                                                relatedFieldLabel="Claim - Please provide a reason"
                                                            />
                                                        </Form.Item>
                                                    </Form>
                                                </>
                                            ),
                                            okText: 'Save',
                                            async onOk() {
                                                await dismissForm.validateFields();

                                                message.loading('Saving...');

                                                mutate(
                                                    {
                                                        id: excessId,
                                                        set: {
                                                            status: item,
                                                            statusReason: dismissForm.getFieldsValue().statusReason,
                                                        },
                                                    },
                                                    {
                                                        onSuccess() {
                                                            message.success(
                                                                `Excess ${item === 'CANCELLED' ? 'cancelled' : 'waived'}.`,
                                                            );
                                                            void trpcUtils.excess.invalidate();
                                                            void trpcUtils.claim.getFinancialSummary.invalidate();
                                                        },
                                                        onError(error) {
                                                            errorMessage.show(error);
                                                        },
                                                    },
                                                );
                                            },
                                        })
                                    }
                                >
                                    {item === 'CANCELLED' ? 'Cancel' : 'Waive'} excess
                                </Button>
                            </Tooltip>
                        ))}
                        rows={[
                            [
                                {
                                    title: 'Contribution method',
                                    children: (
                                        <Tag variant="outlined" color="blue" className="max-w-max text-xs">
                                            {excess && EXCESS_CONTRIBUTION_METHODS_LABELS[excess.contributionMethod]}
                                        </Tag>
                                    ),
                                },
                                {
                                    title: 'Status',
                                    children: (
                                        <Tag
                                            variant="outlined"
                                            color={colors[excess?.status || 'UNRESOLVED']}
                                            className="max-w-max text-xs"
                                        >
                                            {excess && EXCESS_STATUSES_LABELS[excess.status]}
                                        </Tag>
                                    ),
                                },
                            ],
                            [
                                {
                                    title: 'Excess amount',
                                    type: 'currency',
                                    children: excess?.initialAmount,
                                },
                                {
                                    title: 'Received',
                                    type: 'currency',
                                    children: excess?.receivedAmount,
                                },
                                {
                                    title: 'Outstanding',
                                    type: 'currency',
                                    children: excess?.remainingAmount,
                                },
                            ],
                        ]}
                    />

                    {(excess?.status === 'WAIVED' || excess?.status === 'CANCELLED') && (
                        <div className="mb-4">
                            <h4 className="mb-1 font-medium text-(--ant-color-warning-text)">
                                Reason for {excess.status === 'WAIVED' ? 'waive' : 'cancellation'}
                            </h4>
                            <div className="rounded-(--ant-rounded-lg) bg-(--ant-color-warning-bg) p-4">
                                <Typography.Text>{excess.statusReason}</Typography.Text>
                            </div>
                        </div>
                    )}

                    <div className="mb-4 rounded-(--ant-rounded-lg) bg-muted p-4">
                        <Typography.Text>{excess?.description}</Typography.Text>
                    </div>

                    <Tabs
                        items={[
                            {
                                label: (
                                    <div className="flex items-center gap-2">
                                        <DollarSign size={16} />
                                        <span>Excess summary</span>
                                    </div>
                                ),
                                to: '/console/$orgId/claims/details/$id/financials/excesses/details/$excessId/manage',
                            },
                            {
                                label: (
                                    <div className="flex items-center gap-2">
                                        <CreditCard size={16} />
                                        <span>Payment details</span>
                                    </div>
                                ),
                                to: '/console/$orgId/claims/details/$id/financials/excesses/details/$excessId/payments',
                            },
                            {
                                label: (
                                    <div className="flex items-center gap-2">
                                        <FileText size={16} />
                                        <span>Library</span>
                                    </div>
                                ),
                                to: '/console/$orgId/claims/details/$id/financials/excesses/details/$excessId/library',
                            },
                        ]}
                    />
                </>
            )}
        </OverlayPage>
    );
}
